import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Divider,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Stack,
  IconButton,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
  TableChart as TemplateIcon,
  CheckCircle as CreatedIcon,
  SkipNext as SkippedIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  AttachFile as FileIcon,
} from '@mui/icons-material';
import { downloadTemplate, downloadData, parseExcelFile } from '../utils/excelUtils';
import type { ImportExportConfig, ImportSummary } from '../utils/importExportConfigs';
import { ImportCache } from '../utils/importExportConfigs';

// ── Component ─────────────────────────────────────────────────────────────────

interface ImportExportModalProps {
  open: boolean;
  onClose: () => void;
  config: ImportExportConfig;
}

const ImportExportModal: React.FC<ImportExportModalProps> = ({ open, onClose, config }) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importCurrent, setImportCurrent] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [importResult, setImportResult] = useState<ImportSummary | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportFilters, setExportFilters] = useState<Record<string, string>>(() =>
    Object.fromEntries(config.exportFilters.map((f) => [f.key, '']))
  );

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setImportFile(file);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);

    try {
      const rows = await parseExcelFile(importFile);
      setImportTotal(rows.length);
      setImportCurrent(0);

      if (config.importBatch) {
        // Multi-pass batch import (e.g. hierarchical regions — any row ordering)
        const summary = await config.importBatch(rows, (current, total) => {
          setImportCurrent(current);
          setImportTotal(total);
          setImportProgress(Math.round((current / total) * 100));
        });
        setImportResult(summary);
      } else {
        // Standard row-by-row import
        const result: ImportSummary = {
          created: 0,
          updated: 0,
          skipped: 0,
          autoCreatedParents: 0,
          hierarchyConflicts: [],
          unresolvedParents: [],
          reparentingFailed: [],
          errors: [],
        };
        const cache = new ImportCache();

        for (let i = 0; i < rows.length; i++) {
          try {
            const outcome = await config.importRow(rows[i], cache);
            if (outcome.action === 'created') {
              result.created++;
            } else if (outcome.action === 'skipped') {
              // Intentional skip (e.g. duplicate) — not an error.
              result.skipped++;
            } else {
              // action === 'error': validation failure or unresolvable dependency.
              result.errors.push({
                row: i + 3,
                displayName: outcome.displayName || (Object.values(rows[i]).find((v) => v?.trim()) ?? `Row ${i + 3}`),
                field: outcome.field,
                reason: outcome.reason ?? 'Unknown error',
              });
            }
          } catch (err: any) {
            // Uncaught exception from importRow (e.g. network error, thrown grape error).
            const firstValue =
              Object.values(rows[i]).find((v) => v && v.trim()) ?? `Row ${i + 3}`;
            result.errors.push({
              row: i + 3,
              displayName: String(firstValue),
              reason: err?.response?.data?.message ?? err?.message ?? 'Unknown error',
            });
          }
          setImportCurrent(i + 1);
          setImportProgress(Math.round(((i + 1) / rows.length) * 100));
        }

        setImportResult(result);
      }
    } catch (err: any) {
      setImportResult({
        created: 0,
        updated: 0,
        skipped: 0,
        autoCreatedParents: 0,
        hierarchyConflicts: [],
        unresolvedParents: [],
        reparentingFailed: [],
        errors: [{ row: 0, displayName: '', reason: `Failed to parse file: ${err.message}` }],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await config.fetchForExport(exportFilters);
      if (rows.length === 0) {
        alert('No records found matching the selected filters.');
        return;
      }
      // Use exportColumns when defined (e.g. to include derived fields not in the import template)
      downloadData(config.moduleName, config.exportColumns ?? config.columns, rows);
    } catch (err: any) {
      alert(`Export failed: ${err?.message ?? 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    if (importing) return;
    setImportFile(null);
    setImportResult(null);
    setImportProgress(0);
    setImportCurrent(0);
    setImportTotal(0);
    setExportFilters(Object.fromEntries(config.exportFilters.map((f) => [f.key, ''])));
    onClose();
  };

  // ── Shared section styling ──────────────────────────────────────────────────

  const sectionSx = {
    p: 2.5,
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    backgroundColor: alpha(theme.palette.background.default, 0.4),
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          fontWeight: 700,
        }}
      >
        Import / Export — {config.moduleName}
        <IconButton size="small" onClick={handleClose} disabled={importing}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* ── 1. Download Template ─────────────────────────────────────────── */}
        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TemplateIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Download Template
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            An Excel file with column headers and one example row to guide data entry.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => downloadTemplate(config.moduleName, config.columns)}
          >
            Download Template (.xlsx)
          </Button>
        </Box>

        <Divider />

        {/* ── 2. Import ────────────────────────────────────────────────────── */}
        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <UploadIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Import from Excel
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Upload an .xlsx file. Row 1 = headers, row 2 = example (skipped), row 3+ = data.
            Missing linked records are created automatically and flagged for review.
          </Typography>

          {/* File picker */}
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              Choose File
            </Button>
            {importFile && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {importFile.name}
              </Typography>
            )}
          </Box>

          {/* Progress */}
          {importing && (
            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                {importTotal > 0
                  ? `Processing row ${importCurrent} of ${importTotal}…`
                  : 'Preparing import…'}
              </Typography>
              <LinearProgress
                variant={importTotal > 0 ? 'determinate' : 'indeterminate'}
                value={importProgress}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          )}

          {/* Results */}
          {importResult && (
            <Box mt={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
                <Chip
                  icon={<CreatedIcon />}
                  label={`${importResult.created} created`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.success.main, 0.12),
                    color: theme.palette.success.main,
                    fontWeight: 600,
                  }}
                />
                {importResult.updated > 0 && (
                  <Chip
                    icon={<CreatedIcon />}
                    label={`${importResult.updated} updated`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.info?.main ?? theme.palette.primary.main, 0.12),
                      color: theme.palette.info?.main ?? theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  />
                )}
                <Chip
                  icon={<SkippedIcon />}
                  label={`${importResult.skipped} skipped (duplicates)`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.warning.main,
                    fontWeight: 600,
                  }}
                />
                {importResult.autoCreatedParents > 0 && (
                  <Chip
                    label={`${importResult.autoCreatedParents} parents auto-created`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.main,
                      fontWeight: 600,
                    }}
                  />
                )}
                {importResult.reparentingFailed.length > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${importResult.reparentingFailed.length} re-parent failed`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.main,
                      fontWeight: 600,
                    }}
                  />
                )}
                {importResult.errors.length > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${importResult.errors.length} errors`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.error.main, 0.12),
                      color: theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>

              {importResult.errors.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 120,
                    overflowY: 'auto',
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.error.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  }}
                >
                  {importResult.errors.map((e, i) => (
                    <Typography key={i} variant="caption" display="block" color="error">
                      {e.row > 0 ? `Row ${e.row}: ` : ''}
                      {e.displayName ? `"${e.displayName}" — ` : ''}
                      {e.field ? `[${e.field}] ` : ''}
                      {e.reason}
                    </Typography>
                  ))}
                </Box>
              )}

              {importResult.hierarchyConflicts.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 100,
                    overflowY: 'auto',
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.warning.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color="warning.main" display="block" mb={0.5}>
                    Hierarchy conflicts
                  </Typography>
                  {importResult.hierarchyConflicts.map((msg, i) => (
                    <Typography key={i} variant="caption" display="block" color="warning.main">
                      {msg}
                    </Typography>
                  ))}
                </Box>
              )}

              {importResult.unresolvedParents.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 100,
                    overflowY: 'auto',
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.error.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color="error" display="block" mb={0.5}>
                    Unresolved parents
                  </Typography>
                  {importResult.unresolvedParents.map((name, i) => (
                    <Typography key={i} variant="caption" display="block" color="error">
                      "{name}"
                    </Typography>
                  ))}
                </Box>
              )}

              {importResult.reparentingFailed.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 100,
                    overflowY: 'auto',
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.warning.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color="warning.main" display="block" mb={0.5}>
                    Re-parenting failed (backend limitation — parent unchanged)
                  </Typography>
                  {importResult.reparentingFailed.map((name, i) => (
                    <Typography key={i} variant="caption" display="block" color="warning.main">
                      "{name}"
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Button
            variant="contained"
            size="small"
            startIcon={importing ? <CircularProgress size={14} color="inherit" /> : <UploadIcon />}
            disabled={!importFile || importing}
            onClick={handleImport}
            sx={{ mt: importResult ? 1.5 : 0 }}
          >
            {importing ? 'Importing…' : 'Start Import'}
          </Button>
        </Box>

        <Divider />

        {/* ── 3. Export ────────────────────────────────────────────────────── */}
        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <DownloadIcon sx={{ color: theme.palette.info?.main ?? theme.palette.primary.main, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Download Records
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Export existing records to Excel. Apply filters to narrow results.
          </Typography>

          {/* Filter controls */}
          {config.exportFilters.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1.5,
                mb: 2,
              }}
            >
              {config.exportFilters.map((filter) =>
                filter.type === 'select' ? (
                  <FormControl key={filter.key} size="small">
                    <InputLabel>{filter.label}</InputLabel>
                    <Select
                      label={filter.label}
                      value={exportFilters[filter.key] ?? ''}
                      onChange={(e) =>
                        setExportFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))
                      }
                    >
                      {filter.options?.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    key={filter.key}
                    size="small"
                    label={filter.label}
                    value={exportFilters[filter.key] ?? ''}
                    onChange={(e) =>
                      setExportFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))
                    }
                  />
                )
              )}
            </Box>
          )}

          <Button
            variant="contained"
            size="small"
            startIcon={
              exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />
            }
            disabled={exporting}
            onClick={handleExport}
          >
            {exporting ? 'Exporting…' : 'Download Records (.xlsx)'}
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={importing}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportExportModal;
